import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import InfoPage from ".";

const STRIPE_PRICING_SCRIPT_ID = "stripe-pricing-table-script";
const STRIPE_PRICING_SCRIPT_SRC = "https://js.stripe.com/v3/pricing-table.js";

const PartnerCheckout = () => {
  const location = useLocation();
  const [status, setStatus] = useState("loading"); // loading | ok | missing | invalid | error
  const [application, setApplication] = useState(null);

  const token = new URLSearchParams(location.search).get("token");

  useEffect(() => {
    if (!token) {
      setStatus("missing");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/partner/checkout-access?token=${encodeURIComponent(token)}`,
          { method: "GET" }
        );
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.ok) {
          setApplication(data.application || null);
          setStatus("ok");
        } else if (res.status === 404) {
          setStatus("invalid");
        } else if (res.status === 403) {
          setStatus("invalid");
        } else {
          setStatus("error");
        }
      } catch (_) {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (status !== "ok") return;
    if (document.getElementById(STRIPE_PRICING_SCRIPT_ID)) return;
    const script = document.createElement("script");
    script.id = STRIPE_PRICING_SCRIPT_ID;
    script.src = STRIPE_PRICING_SCRIPT_SRC;
    script.async = true;
    document.body.appendChild(script);
  }, [status]);

  const renderBody = () => {
    if (status === "loading") {
      return (
        <div className="partner-checkout-state">
          <p>Checking your approval…</p>
        </div>
      );
    }

    if (status === "missing" || status === "invalid") {
      return (
        <div className="partner-checkout-state">
          <h2 className="partner-checkout-title">This link isn't valid</h2>
          <p className="partner-checkout-sub">
            The approval link is missing or has expired. If you haven't applied
            yet, start your Preferred Partner application below. If you've
            already been approved, check the email we sent you for your secure
            checkout link.
          </p>
          <div className="partner-checkout-actions">
            <a className="partner-checkout-btn primary" href="/partner-signup">
              Apply to be a Preferred Partner
            </a>
            <a className="partner-checkout-btn secondary" href="/contact">
              Contact us
            </a>
          </div>
        </div>
      );
    }

    if (status === "error") {
      return (
        <div className="partner-checkout-state">
          <h2 className="partner-checkout-title">Something went wrong</h2>
          <p className="partner-checkout-sub">
            We couldn't verify your approval right now. Please refresh the page
            or reach out to us and we'll get you sorted.
          </p>
          <div className="partner-checkout-actions">
            <a className="partner-checkout-btn secondary" href="/contact">
              Contact us
            </a>
          </div>
        </div>
      );
    }

    const name = application?.contactName?.split(" ")?.[0] || "there";
    const email = application?.email || undefined;
    return (
      <>
        <div className="partner-checkout-intro">
          <h2 className="partner-checkout-title">You're approved, {name}!</h2>
          <p className="partner-checkout-sub">
            Choose the Preferred Partner plan that best fits{" "}
            {application?.businessName ? (
              <strong>{application.businessName}</strong>
            ) : (
              "your business"
            )}
            . After checkout we'll set up your account within 24 hours and send
            you login details.
          </p>
        </div>

        <div className="partner-checkout-pricing">
          {/* Stripe-hosted pricing table (same table used previously on /partner-signup) */}
          <stripe-pricing-table
            pricing-table-id="prctbl_1TMspsGLCy9PVleCbaXicIKH"
            publishable-key="pk_live_51SDWk7GLCy9PVleCNU1Eow6f4yVZDhCTbLH0vNdUKbBqk05OsixJ2o17BfOwgXv5G69VsQe64z13tpB0nCtkwGrF00w3Ju6FYI"
            {...(email ? { "customer-email": email } : {})}
            client-reference-id={application?.id ? `partner_app_${application.id}` : undefined}
          ></stripe-pricing-table>
        </div>

        <p className="partner-checkout-footer">
          Need a different plan or have questions? <a href="/contact">Contact us</a>.
        </p>
      </>
    );
  };

  const below = (
    <div className="partner-checkout-page">
      {renderBody()}

      <style>{`
        .partner-checkout-page {
          grid-column: 1 / -1;
          width: 100%;
          max-width: 760px;
          margin: 0 auto;
          padding: 24px 24px 56px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        @media (max-width: 768px) {
          .partner-checkout-page { padding: 12px 16px 40px; max-width: 100%; }
        }
        .partner-checkout-intro {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          margin-bottom: 16px;
        }
        .partner-checkout-title {
          font-family: 'Sailors', serif;
          font-size: 30px;
          color: #0f084b;
          margin: 0;
          line-height: 1.2;
        }
        @media (max-width: 768px) {
          .partner-checkout-title { font-size: 22px; }
        }
        .partner-checkout-sub {
          font-family: 'Neue Einstellung', 'Poppins', sans-serif;
          font-size: 15px;
          color: #353945;
          line-height: 1.55;
          margin: 0;
          max-width: 600px;
        }
        .partner-checkout-pricing {
          width: 100%;
          margin: 8px 0 0;
          min-height: 360px;
          display: flex;
          justify-content: center;
        }
        .partner-checkout-pricing stripe-pricing-table {
          display: block;
          width: 100%;
        }
        .partner-checkout-footer {
          margin-top: 24px;
          font-family: 'Neue Einstellung', 'Poppins', sans-serif;
          font-size: 14px;
          color: #353945;
        }
        .partner-checkout-footer a {
          color: #dc1d08;
          text-decoration: none;
          font-weight: 600;
        }
        .partner-checkout-footer a:hover { text-decoration: underline; }

        .partner-checkout-state {
          background: #fff;
          border: 1px solid #E6E8EC;
          border-radius: 16px;
          padding: 32px;
          box-shadow: 0 6px 24px rgba(15, 8, 75, 0.06);
          max-width: 600px;
          width: 100%;
        }
        .partner-checkout-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
          margin-top: 20px;
        }
        .partner-checkout-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 12px 22px;
          border-radius: 100px;
          font-family: 'Neue Einstellung', 'Poppins', sans-serif;
          font-size: 15px;
          font-weight: 600;
          text-decoration: none;
          border: 2px solid transparent;
          min-width: 200px;
        }
        @media (max-width: 768px) {
          .partner-checkout-btn { width: 100%; min-height: 44px; min-width: 0; }
        }
        .partner-checkout-btn.primary {
          background: #dc1d08;
          color: #fff;
          border-color: #dc1d08;
        }
        .partner-checkout-btn.primary:hover {
          background: #b51706;
          border-color: #b51706;
        }
        .partner-checkout-btn.secondary {
          background: transparent;
          color: #0f084b;
          border-color: #0f084b;
        }
        .partner-checkout-btn.secondary:hover {
          background: #0f084b;
          color: #fff;
        }
      `}</style>
    </div>
  );

  return <InfoPage below={below} disableStamps />;
};

export default PartnerCheckout;
